import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { registerDataValidationErrorMatcher } from 'errors/dataValidationError';
import useKeyDownListener from 'hooks/useKeyDownListener';
import CommentIcon from 'images/comment.svg';
import CheckSvg from 'images/v5/icons/check.svg';
import ChevronDownSvg from 'images/v5/icons/chevron-down.svg';
import LayerTwoIcon from 'images/v5/icons/layers-two-01.svg';
import Button from 'layout/atoms/Button/Button';
import Checkbox from 'layout/atoms/Checkbox/Checkbox';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import InputLabel from 'layout/atoms/InputLabel';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import Photo from 'layout/atoms/Photo/Photo';
import Textarea from 'layout/atoms/Textarea/Textarea';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { selectSpecificationComponentHeaders, selectSpecificationComponentHeadersSpecificationId, } from 'store/specificationComponentsSlice';
import { selectSpecificationId } from 'store/specificationDetailsSlice';
import { addProjectionPoint, addProjectionPointImage, removeProjectionPointImage, selectSpecificationProjection, updateProjectionPoint, } from 'store/specificationProjectionsSlice';
import env from 'utils/env';
// function isFormInvalid(form: any) {
//   return Object.keys(form).some((key) => isEmpty(form[key]));
// }
function CommentCreator({ edit }) {
    var _a, _b;
    const { t } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const data = (_a = useSelector(selectModalByName(edit ? 'commentEditor' : 'commentCreator'))) === null || _a === void 0 ? void 0 : _a.data;
    const specificationId = useSelector(selectSpecificationId);
    const cachedSpecificationId = useSelector(selectSpecificationComponentHeadersSpecificationId);
    const cachedComponents = useSelector(selectSpecificationComponentHeaders);
    const modelSpecificationComponents = cachedSpecificationId === specificationId ? cachedComponents : [];
    const modelSpecificationProjectionId = useSelector(selectSpecificationProjection).id;
    const [form, setForm] = useState({
        modelSpecificationProjectionId,
        note: data.note,
        x: data.x,
        y: data.y,
        noteX: data.noteX,
        noteY: data.noteY,
    });
    const [commentType, setCommentType] = useState('DESIGNER');
    const [component, setComponent] = useState(null);
    const [photo, setPhoto] = useState(null);
    const [error] = useState('');
    // const isDisabled = isFormInvalid(form);
    useEffect(() => {
        if (data.picture) {
            setPhoto(data.picture);
        }
        if (data.component) {
            setComponent(data.component);
        }
    }, [data]);
    const handleFormChange = ({ target: { name, value }, }) => {
        setForm((form) => (Object.assign(Object.assign({}, form), { [name]: value })));
    };
    const handlePointAdd = () => {
        // if (!isDisabled) {
        dispatch(addProjectionPoint(Object.assign(Object.assign({}, form), { type: commentType })))
            .unwrap()
            .then((response) => {
            dispatch(updateProjectionPoint({
                projectionPointId: response === null || response === void 0 ? void 0 : response.id,
                note: form.note,
                componentId: component === null || component === void 0 ? void 0 : component.id,
            }));
        });
        dispatch(closeModal({ name: edit ? 'commentEditor' : 'commentCreator' }));
        // }
    };
    const handlePointEdit = () => {
        // if (!isDisabled) {
        dispatch(updateProjectionPoint({
            projectionPointId: data.id,
            note: form.note,
            componentId: component === null || component === void 0 ? void 0 : component.id,
        }));
        dispatch(closeModal({ name: edit ? 'commentEditor' : 'commentCreator' }));
        // }
    };
    const handlePhotoAdd = useCallback((chosenFile) => {
        if (!chosenFile || !data.id)
            return;
        dispatch(addProjectionPointImage({
            projectionPointId: data.id,
            file: chosenFile,
        }))
            .unwrap()
            .then((response) => {
            setPhoto(response.photo.slice(1, -1));
        });
    }, [dispatch, data.id]);
    const handlePhotoRemove = useCallback(() => {
        dispatch(removeProjectionPointImage({
            projectionPointId: data.id,
        }))
            .unwrap()
            .then(() => {
            setPhoto(null);
        });
    }, [dispatch, data.id]);
    registerDataValidationErrorMatcher();
    useKeyDownListener([
        { code: 'Enter', callback: handlePointAdd },
        {
            code: 'Escape',
            callback: () => dispatch(closeModal({ name: edit ? 'commentEditor' : 'commentCreator' })),
        },
    ]);
    return (React.createElement(Modal, { name: edit ? 'commentEditor' : 'commentCreator', size: "xs", header: React.createElement(Label, { weight: "strong" },
            React.createElement(CommentIcon, null),
            " ",
            t('add_comment')), footer: React.createElement(React.Fragment, null,
            React.createElement(Button, { theme: "border", onClick: () => dispatch(closeModal({ name: edit ? 'commentEditor' : 'commentCreator' })) }, t('cancel')),
            React.createElement(Button, { color: "primary", leftIcon: React.createElement(CheckSvg, null), onClick: edit ? handlePointEdit : handlePointAdd, 
                // disabled={isDisabled}
                submit: true }, edit ? t('common:save') : t('add_comment'))) },
        React.createElement(Grid, null,
            edit && (React.createElement(Photo, { size: "s", src: photo ? `${env.static}/${photo}` : null, onPhotoAdd: handlePhotoAdd, onPhotoDelete: handlePhotoRemove })),
            React.createElement("div", null,
                React.createElement(InputLabel, { text: t('enter_comment') }),
                React.createElement(Textarea, { value: form === null || form === void 0 ? void 0 : form.note, onChange: handleFormChange, name: "note" })),
            commentType === 'DESIGNER' && (React.createElement("div", null,
                React.createElement(InputLabel, { text: t('product') }),
                React.createElement(Dropdown, { trigger: component ? (React.createElement(Button, { theme: "border", leftIcon: React.createElement(LayerTwoIcon, null), rightIcon: React.createElement(ChevronDownSvg, null) }, (_b = modelSpecificationComponents.find((item) => item.id === component.id)) === null || _b === void 0 ? void 0 : _b.name)) : (React.createElement(Button, { theme: "border", leftIcon: React.createElement(LayerTwoIcon, null), rightIcon: React.createElement(ChevronDownSvg, null) }, t('select_product'))) }, modelSpecificationComponents.map((component) => (React.createElement(Button, { key: component.id, align: "left", theme: "ghost", onClick: () => setComponent(component) }, component.name)))))),
            React.createElement(Checkbox, { label: t('technologist_comment'), value: commentType === 'TECHNOLOGIST', onClick: () => commentType === 'TECHNOLOGIST'
                    ? setCommentType('DESIGNER')
                    : setCommentType('TECHNOLOGIST') }),
            error && React.createElement("h5", { className: "errorText" }, error))));
}
export default memo(CommentCreator);
